import React, { useEffect, useState, useCallback } from 'react';
import apiClient from '../api/client';
import {
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Stack
} from '@mui/material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    Area,
    ComposedChart
} from 'recharts';

// Custom Tick for multiline date/time
const CustomAxisTick = ({ x, y, payload }) => {
    const d = new Date(payload.value);
    const dateStr = `${d.getMonth() + 1}/${d.getDate()}`;
    const timeStr = `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;

    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} textAnchor="middle" fill="#94a3b8" fontSize={12}>
                {dateStr}
            </text>
            <text x={0} y={0} dy={32} textAnchor="middle" fill="#64748b" fontSize={11}>
                {timeStr}
            </text>
        </g>
    );
};

const HistoryView = ({ ticker = 'SPY' }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchHistory = useCallback(async () => {
        try {
            const res = await apiClient.get(`/data/history?ticker=${ticker}`);
            const histData = res.data;
            // Format timestamps
            const formatted = histData.map(d => {
                const date = new Date(d.timestamp);
                return {
                    ...d,
                    fullTime: date.toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                    }),
                    timestampObj: date.getTime()
                };
            });
            setData(formatted);
        } catch (error) {
            console.error("History Fetch Error", error);
        } finally {
            setLoading(false);
        }
    }, [ticker]);

    useEffect(() => {
        fetchHistory();
        const interval = setInterval(fetchHistory, 60000); // Poll every minute
        return () => clearInterval(interval);
    }, [fetchHistory]);

    const formatGEX = (val) => (val / 1e9).toFixed(2) + 'B';


    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress color="secondary" />
            </Box>
        );
    }

    if (data.length === 0) {
        return (
            <Alert severity="info" sx={{ mt: 2, background: 'rgba(30,41,59,0.5)', color: '#fff' }}>
                Collecting historical data... Wait for the next refresh cycle (1 min).
            </Alert>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, p: 0 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: '#e2e8f0' }}>
                GEX History
            </Typography>

            <Stack spacing={3}>
                {/* Chart 1: Levels vs Price */}
                <Card sx={{ background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(10px)', border: '1px solid #334155' }}>
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h6" sx={{ color: '#94a3b8' }}>
                                🎯 {ticker} Price vs. Key Levels
                            </Typography>
                        </Stack>


                        <ResponsiveContainer width="100%" height={450}>
                            <LineChart data={data} margin={{ top: 20, right: 30, left: 10, bottom: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                                <XAxis
                                    dataKey="timestamp"
                                    stroke="#94a3b8"
                                    tick={<CustomAxisTick />}
                                    minTickGap={60}
                                    height={50}
                                />
                                <YAxis
                                    domain={['auto', 'auto']}
                                    stroke="#94a3b8"
                                    tickFormatter={(v) => `$${v}`}
                                    width={60}
                                />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '8px' }}
                                    labelFormatter={(label) => new Date(label).toLocaleString()}
                                    formatter={(val) => [`$${val.toFixed(2)}`]}
                                />
                                <Legend verticalAlign="top" height={36} iconType="circle" />
                                <Line type="monotone" dataKey="price" stroke="#fff" strokeWidth={3} dot={false} name={`${ticker} Price`} activeDot={{ r: 6 }} />
                                <Line type="stepAfter" dataKey="call_wall" stroke="#4ade80" strokeWidth={2} dot={false} name="Call Wall" />
                                <Line type="stepAfter" dataKey="put_wall" stroke="#f87171" strokeWidth={2} dot={false} name="Put Wall" />
                                <Line type="stepAfter" dataKey="gamma_flip" stroke="#a78bfa" strokeDasharray="5 5" strokeWidth={2} dot={false} name="Gamma Flip" />
                            </LineChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                {/* Chart 2: Net GEX Correlation */}
                <Card sx={{ background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(10px)', border: '1px solid #334155' }}>
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h6" sx={{ color: '#94a3b8' }}>
                                🌊 Net GEX Impact on Price
                            </Typography>
                        </Stack>
                        <ResponsiveContainer width="100%" height={450}>
                            <ComposedChart data={data} margin={{ top: 20, right: 30, left: 10, bottom: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                                <XAxis
                                    dataKey="timestamp"
                                    stroke="#94a3b8"
                                    tick={<CustomAxisTick />}
                                    minTickGap={60}
                                    height={50}
                                />
                                <YAxis yAxisId="left" domain={['auto', 'auto']} stroke="#fff" tickFormatter={(v) => `$${v}`} width={60} />
                                <YAxis yAxisId="right" orientation="right" stroke="#3b82f6" tickFormatter={formatGEX} width={60} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155' }}
                                    labelFormatter={(label) => new Date(label).toLocaleString()}
                                />
                                <Legend verticalAlign="top" height={36} />
                                <Line yAxisId="left" type="monotone" dataKey="price" stroke="#fff" strokeWidth={2} dot={false} name={`${ticker} Price`} />
                                <Area yAxisId="right" type="monotone" dataKey="total_gex" fill="#3b82f6" stroke="#3b82f6" fillOpacity={0.3} name="Net GEX" />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </Stack>
        </Box>
    );
};

export default HistoryView;
